import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { cn } from "@/lib/utils";
import { SnapshotCompare } from "./SnapshotCompare";
import { 
  FileText,
  Globe,
  Plus,
  RefreshCw,
  Trash2,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Upload,
  Search,
  Download,
  Link2,
  FileQuestion,
  Code,
  Brain,
  Sparkles,
  Info,
  Folder
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Circle Dash Meter Component - Compact design
const CircleMeter = ({ score, label, size = 44, reasoning, showTooltip = true }) => {
  const strokeWidth = 3;
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  const getColor = (score) => {
    if (score >= 75) return { stroke: "#22c55e", text: "text-green-500", bg: "bg-green-500/10" };
    if (score >= 50) return { stroke: "#eab308", text: "text-yellow-500", bg: "bg-yellow-500/10" };
    if (score >= 25) return { stroke: "#f97316", text: "text-orange-500", bg: "bg-orange-500/10" };
    return { stroke: "#ef4444", text: "text-red-500", bg: "bg-red-500/10" };
  };
  
  const getBucket = (score) => {
    if (score >= 75) return "Excellent";
    if (score >= 50) return "Good";
    if (score >= 25) return "Fair";
    return "Poor";
  };
  
  const colors = getColor(score);
  const [showTip, setShowTip] = useState(false);
  
  return (
    <div 
      className="relative inline-flex flex-col items-center group"
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
    >
      <div className="relative">
        <svg width={size} height={size} className="transform -rotate-90">
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#27272a"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={colors.stroke}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={cn("text-xs font-bold font-mono", colors.text)}>
            {Math.round(score)}
          </span>
        </div>
      </div>
      <span className="text-[9px] text-zinc-500 font-mono mt-0.5">{label}</span>
      <span className={cn("text-[8px] font-mono px-1 py-0.5 rounded-sm", colors.bg, colors.text)}>
        {getBucket(score)}
      </span>
      
      {/* Tooltip with reasoning */}
      {showTooltip && reasoning && showTip && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-zinc-900 border border-zinc-700 rounded-md shadow-xl">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-zinc-300 leading-relaxed">{reasoning}</p>
          </div>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
            <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-zinc-700" />
          </div>
        </div>
      )}
    </div>
  );
};

// Mini stat badge
const StatBadge = ({ label, value, color = "zinc", icon: Icon }) => {
  const colorClasses = {
    green: "bg-green-500/10 text-green-500 border-green-500/30",
    yellow: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30",
    orange: "bg-orange-500/10 text-orange-500 border-orange-500/30",
    red: "bg-red-500/10 text-red-500 border-red-500/30",
    blue: "bg-blue-500/10 text-blue-500 border-blue-500/30",
    zinc: "bg-zinc-800 text-zinc-400 border-zinc-700",
  };
  
  return (
    <div className={cn(
      "flex items-center gap-1.5 px-2 py-1 rounded-sm border text-xs font-mono",
      colorClasses[color]
    )}>
      {Icon && <Icon className="w-3 h-3" />}
      <span>{label}:</span>
      <span className="font-bold">{value}</span>
    </div>
  );
};

// Site Card Component
const SiteCard = ({ site, onScan, onDelete, onSelect, onRescanErrors, isSelected, errorCount }) => {
  const getStatusIcon = () => {
    switch (site.status) {
      case "COMPLETED":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "SCANNING":
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case "ERROR":
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-zinc-500" />;
    }
  };

  const progress = site.total_pages > 0 
    ? Math.round((site.scanned_pages / site.total_pages) * 100) 
    : 0;

  return (
    <div 
      onClick={() => onSelect(site)}
      className={cn(
        "bg-zinc-900 border rounded-sm p-4 cursor-pointer transition-all hover:border-zinc-600",
        isSelected ? "border-green-500/50 bg-zinc-900/80" : "border-zinc-800"
      )}
      data-testid={`onpage-site-card-${site.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="text-white font-mono text-sm truncate max-w-[180px]" title={site.name}>
            {site.name}
          </span>
        </div>
      </div>
      
      <p className="text-[10px] text-zinc-600 font-mono mb-2 truncate" title={site.domain}>
        {site.domain}
      </p>
      
      {/* Score meters - show whenever scores exist */}
      {(site.avg_eeat_score > 0 || site.avg_hcu_score > 0) && (
        <div className="flex justify-center gap-3 mb-3">
          <CircleMeter score={site.avg_eeat_score || 0} label="EEAT" size={48} showTooltip={false} />
          <CircleMeter score={site.avg_hcu_score || 0} label="HCU" size={48} showTooltip={false} />
        </div>
      )}

      {site.status === "SCANNING" && (
        <div className="mb-3">
          <div className="flex justify-between text-[10px] text-zinc-500 font-mono mb-1">
            <span>Scanning...</span>
            <span>{site.scanned_pages} / {site.total_pages} pages</span>
          </div>
          <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {site.last_scan_at && (
        <p className="text-[10px] text-zinc-600 font-mono mb-2">
          Last: {new Date(site.last_scan_at).toLocaleString()}
        </p>
      )}

      <div className="flex gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
        <SnapshotCompare 
          siteId={site.id} 
          siteName={site.name} 
          monitorType="onpage"
          buttonClassName="text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm font-mono text-[10px] h-7 border-zinc-700"
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onScan(site.id, false)}
          disabled={site.status === "SCANNING"}
          className="flex-1 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm font-mono text-[10px] h-7"
          title="Crawl new pages only"
        >
          <RefreshCw className={cn("w-3 h-3 mr-1", site.status === "SCANNING" && "animate-spin")} />
          New
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onScan(site.id, true)}
          disabled={site.status === "SCANNING"}
          className="flex-1 text-blue-400 hover:text-blue-300 hover:bg-blue-950/30 rounded-sm font-mono text-[10px] h-7"
          title="Full rescan all pages"
        >
          <RefreshCw className="w-3 h-3 mr-1" />
          Full
        </Button>
        {errorCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRescanErrors(site.id)}
            disabled={site.status === "SCANNING"}
            className="flex-1 text-amber-400 hover:text-amber-300 hover:bg-amber-950/30 rounded-sm font-mono text-[10px] h-7"
            title={`Rescan ${errorCount} error pages`}
          >
            <AlertTriangle className="w-3 h-3 mr-1" />
            Retry ({errorCount})
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(site.id)}
          className="text-red-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm font-mono text-[10px] h-7 px-2"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

// Page Row Component
const PageRow = ({ page, isExpanded, onToggle }) => {
  const getContentColor = (bucket) => {
    switch (bucket) {
      case "excellent": return "green";
      case "good": return "blue";
      case "medium": return "yellow";
      default: return "red";
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
      <div 
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-zinc-800/50"
        onClick={onToggle}
      >
        <button className="text-zinc-500">
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        <div className="flex-1 min-w-0">
          <p className="text-xs text-zinc-300 font-mono truncate" title={page.url}>
            {page.url.replace(/^https?:\/\/[^/]+/, '')}
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatBadge 
            label="Words" 
            value={page.word_count} 
            color={getContentColor(page.content_bucket)}
          />
          <StatBadge 
            label="Links" 
            value={page.internal_links_count} 
            color={page.has_low_internal_links ? "red" : "green"}
            icon={Link2}
          />
          {page.has_faq && (
            <StatBadge label="FAQ" value="✓" color="green" icon={FileQuestion} />
          )}
          {page.has_schema && (
            <StatBadge label="Schema" value="✓" color="green" icon={Code} />
          )}
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <CircleMeter 
            score={page.eeat_score || 0} 
            label="EEAT" 
            size={40} 
            reasoning={page.eeat_reasoning}
          />
          <CircleMeter 
            score={page.hcu_score || 0} 
            label="HCU" 
            size={40} 
            reasoning={page.hcu_reasoning}
          />
        </div>
        
        <a 
          href={page.url} 
          target="_blank" 
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-zinc-500 hover:text-white p-1"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
      
      {isExpanded && (
        <div className="border-t border-zinc-800 p-4 bg-zinc-950/50">
          <div className="grid grid-cols-2 gap-4">
            {/* Content Analysis */}
            <div>
              <h4 className="text-xs text-zinc-400 font-mono mb-2 flex items-center gap-1">
                <FileText className="w-3 h-3" /> Content Analysis
              </h4>
              <div className="space-y-1 text-xs font-mono">
                <p className="text-zinc-300">
                  Word Count: <span className={cn(
                    page.content_bucket === "excellent" ? "text-green-500" :
                    page.content_bucket === "good" ? "text-blue-500" :
                    page.content_bucket === "medium" ? "text-yellow-500" : "text-red-500"
                  )}>{page.word_count}</span>
                  <span className="text-zinc-500 ml-2">({page.content_bucket})</span>
                </p>
                <p className="text-zinc-300">
                  FAQ Section: {page.has_faq ? (
                    <span className="text-green-500">Yes ({page.faq_type})</span>
                  ) : (
                    <span className="text-red-500">No</span>
                  )}
                </p>
              </div>
            </div>
            
            {/* Technical SEO */}
            <div>
              <h4 className="text-xs text-zinc-400 font-mono mb-2 flex items-center gap-1">
                <Code className="w-3 h-3" /> Technical SEO
              </h4>
              <div className="space-y-1 text-xs font-mono">
                <p className="text-zinc-300">
                  Internal Links: <span className={page.has_low_internal_links ? "text-red-500" : "text-green-500"}>
                    {page.internal_links_count}
                  </span>
                  {page.has_low_internal_links && <span className="text-red-500 ml-1">(Low)</span>}
                </p>
                <p className="text-zinc-300">
                  Schemas: {page.schema_types?.length > 0 ? (
                    <span className="text-green-500">{page.schema_types.join(", ")}</span>
                  ) : (
                    <span className="text-red-500">None</span>
                  )}
                </p>
              </div>
            </div>
            
            {/* EEAT Breakdown */}
            <div>
              <h4 className="text-xs text-zinc-400 font-mono mb-2 flex items-center gap-1">
                <Brain className="w-3 h-3" /> EEAT Breakdown
              </h4>
              <div className="grid grid-cols-4 gap-2 mb-2">
                <div className="text-center">
                  <div className="text-lg font-bold text-zinc-300">{Math.round(page.eeat_expertise || 0)}</div>
                  <div className="text-[9px] text-zinc-500">Expertise</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-zinc-300">{Math.round(page.eeat_experience || 0)}</div>
                  <div className="text-[9px] text-zinc-500">Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-zinc-300">{Math.round(page.eeat_authority || 0)}</div>
                  <div className="text-[9px] text-zinc-500">Authority</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-zinc-300">{Math.round(page.eeat_trust || 0)}</div>
                  <div className="text-[9px] text-zinc-500">Trust</div>
                </div>
              </div>
              {page.eeat_reasoning && (
                <p className="text-[10px] text-zinc-500 italic bg-zinc-900 p-2 rounded">
                  {page.eeat_reasoning}
                </p>
              )}
            </div>
            
            {/* HCU Breakdown */}
            <div>
              <h4 className="text-xs text-zinc-400 font-mono mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> HCU Breakdown
              </h4>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div className="text-center">
                  <div className="text-lg font-bold text-zinc-300">{Math.round(page.hcu_helpfulness || 0)}</div>
                  <div className="text-[9px] text-zinc-500">Helpfulness</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-zinc-300">{Math.round(page.hcu_originality || 0)}</div>
                  <div className="text-[9px] text-zinc-500">Originality</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-zinc-300">{Math.round(page.hcu_comprehensiveness || 0)}</div>
                  <div className="text-[9px] text-zinc-500">Comprehensive</div>
                </div>
              </div>
              {page.hcu_reasoning && (
                <p className="text-[10px] text-zinc-500 italic bg-zinc-900 p-2 rounded">
                  {page.hcu_reasoning}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main OnPage Monitor Component
export const OnPageMonitor = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [pages, setPages] = useState([]);
  const [stats, setStats] = useState(null);
  const [siteErrorCounts, setSiteErrorCounts] = useState({});
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [siteName, setSiteName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageFilter, setPageFilter] = useState("all");
  const [selectedSitemapType, setSelectedSitemapType] = useState("all");
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [expandedPage, setExpandedPage] = useState(null);
  
  // Groups
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [filterGroup, setFilterGroup] = useState("all");

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/groups`);
      setGroups(response.data);
    } catch (e) {
      console.error("Failed to fetch groups:", e);
    }
  }, []);

  const toggleGroupSelection = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const filteredSites = sites.filter((site) => {
    // Group filter
    if (filterGroup !== "all") {
      if (!site.groups || !site.groups.includes(filterGroup)) {
        return false;
      }
    }
    // Search filter
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      site.name?.toLowerCase().includes(query) ||
      site.domain?.toLowerCase().includes(query)
    );
  });

  const fetchErrorCounts = useCallback(async (siteList) => {
    const counts = {};
    for (const site of siteList) {
      if (site.status === 'COMPLETED') {
        try {
          const response = await axios.get(`${API}/onpage-monitor/${site.id}/stats`);
          counts[site.id] = response.data.errors || 0;
        } catch (e) {
          counts[site.id] = 0;
        }
      }
    }
    setSiteErrorCounts(counts);
  }, []);

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/onpage-monitor`);
      setSites(response.data);
      fetchErrorCounts(response.data);
      
      if (response.data.length > 0 && !selectedSite) {
        setSelectedSite(response.data[0]);
      }
    } catch (e) {
      console.error("Failed to fetch sites:", e);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSite, fetchErrorCounts]);

  const fetchStats = useCallback(async () => {
    if (!selectedSite) return;
    
    try {
      const response = await axios.get(`${API}/onpage-monitor/${selectedSite.id}/stats`);
      setStats(response.data);
    } catch (e) {
      console.error("Failed to fetch stats:", e);
    }
  }, [selectedSite]);

  const fetchPages = useCallback(async () => {
    if (!selectedSite) return;
    
    setIsLoadingPages(true);
    try {
      const params = new URLSearchParams();
      if (pageFilter !== "all") params.append('filter', pageFilter);
      if (selectedSitemapType && selectedSitemapType !== "all") params.append('sitemap_type', selectedSitemapType);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await axios.get(`${API}/onpage-monitor/${selectedSite.id}/pages${queryString}`);
      setPages(response.data);
    } catch (e) {
      console.error("Failed to fetch pages:", e);
    } finally {
      setIsLoadingPages(false);
    }
  }, [selectedSite, pageFilter, selectedSitemapType]);

  useEffect(() => {
    fetchSites();
    fetchGroups();
    const interval = setInterval(fetchSites, 10000);
    return () => clearInterval(interval);
  }, [fetchSites, fetchGroups]);

  // Reset sitemap type filter only when site changes
  useEffect(() => {
    if (selectedSite) {
      setSelectedSitemapType("all");
    }
  }, [selectedSite?.id]);

  // Fetch stats and pages when site, filter, or sitemap type changes
  useEffect(() => {
    if (selectedSite) {
      fetchStats();
      fetchPages();
    }
  }, [selectedSite, pageFilter, selectedSitemapType, fetchStats, fetchPages]);

  const handleAddSite = async () => {
    if (!sitemapUrl) {
      toast.error("Please enter a sitemap URL");
      return;
    }

    setIsAdding(true);
    try {
      const response = await axios.post(`${API}/onpage-monitor`, {
        sitemap_url: sitemapUrl,
        name: siteName,
        groups: selectedGroups
      });
      toast.success("Site added - scanning started");
      setSitemapUrl("");
      setSiteName("");
      setSelectedGroups([]);
      fetchSites();
      setSelectedSite(response.data);
    } catch (e) {
      const message = e.response?.data?.detail || "Failed to add site";
      toast.error(message);
    } finally {
      setIsAdding(false);
    }
  };

  const handleScan = async (siteId, full = false) => {
    try {
      await axios.post(`${API}/onpage-monitor/${siteId}/scan?full=${full}&ai=true`);
      toast.success(full ? "Full scan started (with AI analysis)" : "Scan started");
      fetchSites();
    } catch (e) {
      toast.error("Failed to start scan");
    }
  };

  const handleRescanErrors = async (siteId) => {
    try {
      const response = await axios.post(`${API}/onpage-monitor/${siteId}/rescan-errors`);
      if (response.data.error_count > 0) {
        toast.success(`Rescanning ${response.data.error_count} error pages`);
      } else {
        toast.info("No error pages to rescan");
      }
      fetchSites();
    } catch (e) {
      toast.error("Failed to start rescan");
    }
  };

  const handleDelete = async (siteId) => {
    if (!window.confirm("Delete this site and all its data?")) return;
    
    try {
      await axios.delete(`${API}/onpage-monitor/${siteId}`);
      toast.success("Site deleted");
      if (selectedSite?.id === siteId) {
        setSelectedSite(null);
        setPages([]);
        setStats(null);
      }
      fetchSites();
    } catch (e) {
      toast.error("Failed to delete site");
    }
  };

  const handleCsvImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/onpage-monitor/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { added, skipped, errors } = response.data;
      
      if (added.length > 0) toast.success(`Imported ${added.length} site(s)`);
      if (skipped.length > 0) toast.info(`Skipped ${skipped.length} duplicate(s)`);
      if (errors.length > 0) toast.error(`${errors.length} URL(s) had errors`);
      
      fetchSites();
    } catch (e) {
      toast.error("Failed to import CSV");
    } finally {
      setIsImporting(false);
      event.target.value = '';
    }
  };

  const handleExportCsv = () => {
    if (!selectedSite) return;
    const filterParam = pageFilter !== "all" ? `?filter=${pageFilter}` : "";
    window.open(`${API}/onpage-monitor/${selectedSite.id}/export-csv${filterParam}`, '_blank');
    toast.success("Export started");
  };

  const filters = [
    { id: "all", label: "All Pages" },
    { id: "low_content", label: "Low Content" },
    { id: "no_faq", label: "No FAQ" },
    { id: "low_links", label: "Low Links" },
    { id: "no_schema", label: "No Schema" },
    { id: "low_eeat", label: "Low EEAT" },
    { id: "low_hcu", label: "Low HCU" },
    { id: "errors", label: "Errors" },
  ];

  return (
    <div className="p-6" data-testid="onpage-monitor">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white font-mono flex items-center gap-2">
          <FileText className="w-6 h-6 text-green-500" />
          OnPage Monitoring
        </h1>
        <p className="text-zinc-500 text-sm font-mono mt-1">
          Content length, FAQ, internal links, schemas, EEAT & HCU analysis
        </p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left Panel - Sites List */}
        <div className="col-span-4">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
            <h2 className="text-sm font-mono text-white mb-4 flex items-center gap-2">
              <Globe className="w-4 h-4 text-green-500" />
              MONITORED SITES
            </h2>

            {/* Add Site Form */}
            <div className="space-y-2 mb-4">
              <Input
                placeholder="Site name (optional)"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white font-mono text-xs h-8"
                data-testid="onpage-site-name-input"
              />
              <div className="flex gap-2">
                <Input
                  placeholder="https://example.com/sitemap.xml"
                  value={sitemapUrl}
                  onChange={(e) => setSitemapUrl(e.target.value)}
                  className="flex-1 bg-zinc-800 border-zinc-700 text-white font-mono text-xs h-8"
                  data-testid="onpage-sitemap-input"
                />
                <Button
                  onClick={handleAddSite}
                  disabled={isAdding || !sitemapUrl}
                  className="bg-green-600 hover:bg-green-700 text-white font-mono text-xs h-8 px-3"
                  data-testid="onpage-add-btn"
                >
                  {isAdding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Group Selection */}
              {groups.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] text-zinc-500 font-mono">Groups:</span>
                  {groups.map((group) => (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => toggleGroupSelection(group.id)}
                      className={cn(
                        "flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors",
                        selectedGroups.includes(group.id)
                          ? "bg-zinc-700 text-white"
                          : "bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800"
                      )}
                    >
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: group.color }} />
                      {group.name}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Import CSV */}
              <div className="flex gap-2">
                <label className="flex-1">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCsvImport}
                    className="hidden"
                    data-testid="onpage-csv-input"
                  />
                  <Button
                    variant="outline"
                    className="w-full border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 font-mono text-xs h-8"
                    disabled={isImporting}
                    asChild
                  >
                    <span>
                      <Upload className="w-3 h-3 mr-1" />
                      {isImporting ? "Importing..." : "Import CSV"}
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            {/* Search and Group Filter */}
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  placeholder="Search sites..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 bg-zinc-800 border-zinc-700 text-white font-mono text-xs h-8"
                  data-testid="onpage-search-input"
                />
              </div>
              {groups.length > 0 && (
                <select
                  value={filterGroup}
                  onChange={(e) => setFilterGroup(e.target.value)}
                  className="bg-zinc-800 border border-zinc-700 rounded-sm px-2 py-1 text-[10px] text-zinc-300 font-mono h-8"
                  data-testid="onpage-group-filter"
                >
                  <option value="all">All Groups</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Sites List */}
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-zinc-800 border border-zinc-700 rounded-sm p-4 animate-pulse">
                    <div className="h-4 bg-zinc-700 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-zinc-700 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : filteredSites.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 font-mono text-sm">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No sites yet</p>
                <p className="text-xs mt-1">Add a sitemap URL to start</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[calc(100vh-420px)] overflow-y-auto pr-1">
                {filteredSites.map((site) => (
                  <SiteCard
                    key={site.id}
                    site={site}
                    onScan={handleScan}
                    onDelete={handleDelete}
                    onSelect={setSelectedSite}
                    onRescanErrors={handleRescanErrors}
                    isSelected={selectedSite?.id === site.id}
                    errorCount={siteErrorCounts[site.id] || 0}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Site Details */}
        <div className="col-span-8">
          {selectedSite ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-mono text-white flex items-center gap-2">
                  <FileText className="w-4 h-4 text-green-500" />
                  {selectedSite.name}
                </h2>
                <span className="text-xs text-zinc-500 font-mono">
                  {selectedSite.total_pages} pages
                </span>
              </div>

              {/* Stats Grid */}
              {stats && (
                <div className="grid grid-cols-6 gap-3 mb-4">
                  {/* Content Distribution */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                    <div className="text-[10px] text-zinc-500 font-mono mb-1">CONTENT</div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-green-500">Excellent</span>
                        <span className="text-white">{stats.excellent_content}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-blue-500">Good</span>
                        <span className="text-white">{stats.good_content}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-yellow-500">Medium</span>
                        <span className="text-white">{stats.medium_content}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-red-500">Low</span>
                        <span className="text-white">{stats.low_content}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* FAQ */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                    <div className="text-[10px] text-zinc-500 font-mono mb-1">FAQ</div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-green-500">With FAQ</span>
                        <span className="text-white">{stats.with_faq}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-red-500">No FAQ</span>
                        <span className="text-white">{stats.without_faq}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Links */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                    <div className="text-[10px] text-zinc-500 font-mono mb-1">LINKS</div>
                    <div className="flex justify-between text-xs font-mono">
                      <span className="text-red-500">Low Links</span>
                      <span className="text-white">{stats.low_internal_links}</span>
                    </div>
                  </div>
                  
                  {/* Schema */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                    <div className="text-[10px] text-zinc-500 font-mono mb-1">SCHEMA</div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-green-500">Has Schema</span>
                        <span className="text-white">{stats.with_schema}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-red-500">No Schema</span>
                        <span className="text-white">{stats.without_schema}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* EEAT Average */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3 flex flex-col items-center justify-center">
                    <CircleMeter 
                      score={stats.avg_eeat_score || 0} 
                      label="Avg EEAT" 
                      size={52}
                      showTooltip={false}
                    />
                  </div>
                  
                  {/* HCU Average */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-3 flex flex-col items-center justify-center">
                    <CircleMeter 
                      score={stats.avg_hcu_score || 0} 
                      label="Avg HCU" 
                      size={52}
                      showTooltip={false}
                    />
                  </div>
                </div>
              )}

              {/* Filter Tabs */}
              <div className="flex gap-1 mb-4 overflow-x-auto items-center">
                {filters.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setPageFilter(f.id)}
                    className={cn(
                      "px-3 py-1.5 font-mono text-xs rounded-sm transition-colors whitespace-nowrap",
                      pageFilter === f.id 
                        ? "bg-zinc-700 text-white" 
                        : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
                    )}
                  >
                    {f.label}
                  </button>
                ))}
                <div className="flex-1" />
                {/* Sitemap Type Filter */}
                {selectedSite?.sitemap_types && selectedSite.sitemap_types.length > 1 && (
                  <select
                    value={selectedSitemapType}
                    onChange={(e) => setSelectedSitemapType(e.target.value)}
                    className="bg-zinc-900 border border-zinc-700 rounded-sm text-xs font-mono px-2 py-1 text-zinc-300 focus:outline-none focus:border-zinc-500 mr-2"
                    data-testid="sitemap-type-filter"
                  >
                    <option value="all">All Types</option>
                    {selectedSite.sitemap_types.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCsv}
                  disabled={pages.length === 0}
                  className="border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-sm font-mono text-xs h-7"
                  data-testid="export-onpage-csv-btn"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Export CSV
                </Button>
              </div>

              {/* Pages List */}
              <div className="space-y-2 max-h-[calc(100vh-450px)] overflow-y-auto">
                {isLoadingPages ? (
                  [1, 2, 3].map((i) => (
                    <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 animate-pulse">
                      <div className="h-4 bg-zinc-800 rounded w-3/4" />
                    </div>
                  ))
                ) : pages.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500 font-mono text-sm">
                    <p>No pages in this category</p>
                  </div>
                ) : (
                  pages.map((page) => (
                    <PageRow
                      key={page.id}
                      page={page}
                      isExpanded={expandedPage === page.id}
                      onToggle={() => setExpandedPage(expandedPage === page.id ? null : page.id)}
                    />
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-8 flex items-center justify-center h-[calc(100vh-200px)]">
              <div className="text-center text-zinc-500 font-mono">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="text-sm">Select a site to view details</p>
                <p className="text-xs mt-1">or add a new sitemap URL</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnPageMonitor;
